import {
    Alert,
    Autocomplete,
    Button,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FieldMapper from "../FieldMapper";
import TemplateTextField from "../TemplateTextField";
import FlussConditionBuilder from "../FlussConditionBuilder";
import QueueSelect from "../QueueSelect";
import TranslatableInput from "component/global/translation/TranslatableInput";
import StudioCodeEditor from "modules/studio/components/StudioCodeEditor";
import {
    FieldMapping,
    QueueSelection,
    ScopeOption,
} from "../fluss-builder-types";
import { FactoryActionCards, FactoryActionParamForm, parseFactoryParams } from "./FactoryActionForm";
import ServiceOutputMapper from "./ServiceOutputMapper";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import { FlussMetadataOption, FlussNodePanelData } from "./fluss-node-panel-data";
import {
    metadataToCreatableScopeOptions,
    parseMapping,
    primaryBindingFor,
    resolveAnchorTable,
    serializeMapping,
    factoryActionsForNodeKind,
    ensureBinding,
    updateBinding,
} from "./fluss-node-panel-utils";
import { useFlussTableColumns } from "./useFlussNodePanelMetadata";
import { renderTableOption, sortedTableOptions } from "./TableOption";
import { useDataCatalogContext } from "../data-picker/DataCatalogContext";
import { RecordSourceBindingField } from "./RecordSourceField";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";

// Process variable names offered as targets/suggestions (output mappings), from the single catalog.
export const processVariableOptions = (catalog: { process_variables: { outputs: { key: string; type: string }[] } }): ScopeOption[] =>
    catalog.process_variables.outputs.map((output) => ({ name: output.key, label: output.key, type: output.type || "" }));

export interface ActivityPanelProps {
    selection: FlussElementSelection;
    modeler: any;
    data: FlussNodePanelData;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

export const NodeNameField = ({
    selection,
    modeler,
    readOnly,
    onChanged,
}: Pick<ActivityPanelProps, "selection" | "modeler" | "readOnly" | "onChanged">) => {
    const { t } = useTranslation();
    return (
        <FlussNodePanelField label={t("fluss.node_panel.name", { defaultValue: "Node name" })}>
            <TextField
                size="small"
                value={selection.businessObject?.name || ""}
                disabled={readOnly}
                inputProps={{
                    "aria-label": String(t("fluss.node_panel.name", { defaultValue: "Node name" })),
                }}
                onChange={(event) => {
                    modeler?.get("modeling")?.updateProperties(selection.element, {
                        name: event.target.value,
                    });
                    onChanged();
                }}
            />
        </FlussNodePanelField>
    );
};

export const TableSelect = ({
    value,
    tables,
    label,
    readOnly,
    onChange,
}: {
    value: string;
    tables: FlussMetadataOption[];
    label: string;
    readOnly: boolean;
    onChange: (value: string) => void;
}) => {
    const { t } = useTranslation();
    return (<Autocomplete
        size="small"
        fullWidth
        disabled={readOnly}
        options={sortedTableOptions(tables, (table) => String(t(String(table.label)) || table.name))}
        renderOption={(props, option) => renderTableOption(props, option, String(t(String(option.label)) || option.name))}
        value={tables.find((table) => table.name === value) || null}
        getOptionLabel={(option) => t(String(option.label)) || option.name}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        onChange={(_, option) => onChange(option?.name || "")}
        renderInput={(params) => <TextField {...params} placeholder={String(t("fluss.node_panel.table_choose", { defaultValue: "Choose a table…" }))} inputProps={{ ...params.inputProps, "aria-label": label }} />}
    />);
};

// PRIM-024 (round 6): assigneeUser (a literal sys_users.id) and assigneeExpression (a
// record.<col>/var.<name> reference to resolve at runtime) used to collapse into the same
// "record_person" mode here — a fixed user, once saved, was reopened as "Person from record"
// with a source that isn't actually a valid record./var. reference. The two config keys always
// meant different things on the backend (FlussEngine::AssigneeResolver#by_user_like checks
// assigneeUser before assigneeExpression); the read side just never told them apart.
const assignmentFromBinding = (binding: any): QueueSelection => {
    if (binding?.assigneeRole) return { mode: "queue", roleName: binding.assigneeRole };
    if (binding?.assigneeUser) return { mode: "fixed_user", userId: String(binding.assigneeUser) };
    if (binding?.assigneeExpression) return { mode: "record_person", source: binding.assigneeExpression };
    return null;
};

const resolveQueueRoleLabel = (roles: FlussMetadataOption[], roleName: string) => {
    const role = roles.find((candidate) => candidate.name === roleName);
    return role?.label || role?.name || roleName;
};

export const UserTaskNodePanel = (props: ActivityPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const binding = primaryBindingFor(selection.businessObject);
    const [taskKind, setTaskKind] = useState(binding?.kind === "approval" ? "approval" : "assignment");
    const [targetTable, setTargetTable] = useState(
        String(binding?.target_table || binding?.targetTable || resolveAnchorTable(modeler) || "")
    );
    const [mappings, setMappings] = useState<FieldMapping[]>(parseMapping(binding?.field_mappings || binding?.fieldMappings));
    // PRIM-026 (round 8, Bloco B): the User Task's work record is either a mapped record
    // (existing "Create record in" + Created record fields) or a service form (this task) --
    // mutually exclusive, so a toggle between the two.
    const [workSource, setWorkSource] = useState<"record" | "form">(binding?.formId ? "form" : "record");
    const [formId, setFormId] = useState(String(binding?.formId || ""));
    const [formMode, setFormMode] = useState(String(binding?.formMode || "create"));
    const [waitForCompletion, setWaitForCompletion] = useState(
        binding?.waitForCompletion === "true" || binding?.waitForCompletion === true,
    );
    const [waitCondition, setWaitCondition] = useState(String(binding?.waitCondition || ""));
    const [outputMappings, setOutputMappings] = useState<FieldMapping[]>(parseMapping(binding?.variableMappings));
    const [assignment, setAssignment] = useState<QueueSelection>(assignmentFromBinding(binding));
    const [taskTitle, setTaskTitle] = useState(String(binding?.taskTitle || ""));
    const [instructions, setInstructions] = useState(String(binding?.instructions || ""));
    const catalog = useDataCatalogContext();
    const columnsQuery = useFlussTableColumns(targetTable, data.tables);
    const fields = useMemo(
        () => metadataToCreatableScopeOptions(columnsQuery.data || []),
        [columnsQuery.data]
    );
    const hasClassField = fields.some((field) => field.name === "sys_class_name");
    const visibleFields = useMemo(() => fields.filter((field) => field.name !== "sys_class_name"), [fields]);
    // `record.` in the wait condition is the task's work record when this task creates one.
    const waitRecordTable = workSource === "record" && targetTable ? targetTable : undefined;
    const outputTargets = useMemo(() => {
        const known = new Map<string, ScopeOption>(catalog.process_variables.outputs.map((output) => [output.key, {
            name: output.key, label: output.key, type: output.type || "",
        }]));
        outputMappings.forEach((mapping) => {
            // PRIM-019 (round 6): "" (unknown), not "TEXT" — these are targets fabricated from a
            // mapping row before their real type is known anywhere else. Claiming TEXT here
            // never actually blocked anything (typeFamily("TEXT") accepts any source), but it
            // was dishonest about what's actually known.
            if (!known.has(mapping.target)) known.set(mapping.target, {
                name: mapping.target,
                label: mapping.target,
                type: "",
            });
        });
        return Array.from(known.values());
    }, [outputMappings, catalog]);

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setTaskKind(current?.kind === "approval" ? "approval" : "assignment");
        setTargetTable(
            String(current?.target_table || current?.targetTable || resolveAnchorTable(modeler) || "")
        );
        setMappings(parseMapping(current?.field_mappings || current?.fieldMappings));
        setWorkSource(current?.formId ? "form" : "record");
        setFormId(String(current?.formId || ""));
        setFormMode(String(current?.formMode || "create"));
        setWaitForCompletion(current?.waitForCompletion === "true" || current?.waitForCompletion === true);
        setWaitCondition(String(current?.waitCondition || ""));
        setOutputMappings(parseMapping(current?.variableMappings));
        setAssignment(assignmentFromBinding(current));
        setTaskTitle(String(current?.taskTitle || ""));
        setInstructions(String(current?.instructions || ""));
    }, [selection.id, selection.businessObject, modeler]);

    const changeBinding = (kind: string, updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, kind, updates);
        onChanged();
    };

    useEffect(() => {
        if (!targetTable || workSource !== "record" || !hasClassField || readOnly) return;
        if (mappings.some((mapping) => mapping.target === "sys_class_name" && mapping.source === targetTable)) return;
        const next = [
            ...mappings.filter((mapping) => mapping.target !== "sys_class_name"),
            { target: "sys_class_name", source: targetTable },
        ];
        setMappings(next);
        updateBinding(selection, modeler, taskKind, { target_table: targetTable, field_mappings: serializeMapping(next) });
        onChanged();
    }, [targetTable, workSource, hasClassField, mappings, readOnly, selection, modeler, taskKind, onChanged]);

    const queueRoleLabel = assignment?.mode === "queue"
        ? resolveQueueRoleLabel(data.roles, assignment.roleName)
        : "";

    const changeAssignment = (next: QueueSelection) => {
        setAssignment(next);
        if (next?.mode === "queue") {
            changeBinding(taskKind, {
                assigneeRole: next.roleName,
                assigneeUser: "",
                assigneeExpression: "",
            });
        } else if (next?.mode === "fixed_user") {
            // PRIM-024: a literal id goes in assigneeUser, never assigneeExpression — that field
            // is for a record./var. reference to resolve, not a value to use verbatim.
            changeBinding(taskKind, {
                assigneeRole: "",
                assigneeUser: next.userId || "",
                assigneeExpression: "",
            });
        } else {
            changeBinding(taskKind, {
                assigneeRole: "",
                assigneeUser: "",
                assigneeExpression: (next?.mode === "record_person" && next.source) || "",
            });
        }
    };

    const taskTextFields = <>
        <FlussNodePanelField label={t("fluss.template.task_title", { defaultValue: "Task title" })} fieldPath="taskTitle">
            <TemplateTextField label={String(t("fluss.template.task_title", { defaultValue: "Task title" }))} value={taskTitle} disabled={readOnly}
                onChange={(next) => { setTaskTitle(next); changeBinding(taskKind, { taskTitle: next }); }} />
        </FlussNodePanelField>
        <FlussNodePanelField label={t("fluss.template.instructions", { defaultValue: "Instructions" })} fieldPath="instructions">
            <TemplateTextField label={String(t("fluss.template.instructions", { defaultValue: "Instructions" }))} value={instructions} multiline disabled={readOnly}
                onChange={(next) => { setInstructions(next); changeBinding(taskKind, { instructions: next }); }} />
        </FlussNodePanelField>
    </>;

    if (taskKind === "approval") {
        return (
            <FlussNodePanelShell
                selection={selection}
                kind="approval"
                canViewAdvanced={canViewAdvanced}
                tabs={[{
                    key: "approval",
                    label: t("fluss.node_panel.tab.approval", { defaultValue: "Approval" }),
                    content: (
                        <Stack spacing={1.25}>
                            <FlussNodePanelField
                                label={t("fluss.node_panel.work_type", { defaultValue: "Work type" })}
                            >
                                <TextField
                                    select
                                    size="small"
                                    value={taskKind}
                                    disabled={readOnly}
                                    onChange={(event) => {
                                        const next = event.target.value;
                                        setTaskKind(next);
                                        changeBinding(next, { kind: next });
                                    }}
                                >
                                    <MenuItem value="assignment">
                                        {t("fluss.node_panel.work_type.task", { defaultValue: "User task" })}
                                    </MenuItem>
                                    <MenuItem value="approval">
                                        {t("fluss.node_panel.work_type.approval", { defaultValue: "Approval" })}
                                    </MenuItem>
                                </TextField>
                            </FlussNodePanelField>
                            <Typography variant="caption" color="text.secondary">
                                {t("fluss.node_panel.approval.work_type_help", {
                                    defaultValue: "A aprovação oferece os resultados Aprovar / Rejeitar / Solicitar mais informações.",
                                })}
                            </Typography>
                            <NodeNameField {...props} />
                            {taskTextFields}
                            <FlussNodePanelField
                                label={t("fluss.node_panel.approval.approver", { defaultValue: "Approver" })}
                                fieldPath="assigneeExpression"
                            >
                                <ToggleButtonGroup
                                    exclusive
                                    fullWidth
                                    size="small"
                                    value={assignment?.mode || "queue"}
                                    disabled={readOnly}
                                    onChange={(_, mode) => mode && changeAssignment(
                                        mode === "queue"
                                            ? { mode: "queue", roleName: data.queues[0]?.roleName || "" }
                                            : mode === "fixed_user"
                                                ? { mode: "fixed_user", userId: "" }
                                                : { mode: "record_person", source: undefined }
                                    )}
                                >
                                    <ToggleButton value="queue">
                                        {t("fluss.node_panel.assignment.queue", { defaultValue: "Queue" })}
                                    </ToggleButton>
                                    <ToggleButton value="record_person">
                                        {t("fluss.node_panel.assignment.person", { defaultValue: "Person from record" })}
                                    </ToggleButton>
                                    <ToggleButton value="fixed_user">
                                        {t("fluss.node_panel.assignment.fixed_user", { defaultValue: "Fixed user" })}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <QueueSelect
                                    value={assignment}
                                    onChange={changeAssignment}
                                    roles={data.roles}
                                    groups={data.groups}
                                    groupRoles={data.groupRoles}
                                    lockedMode={assignment?.mode || "queue"}
                                    disabled={readOnly}
                                />
                            </FlussNodePanelField>
                            <Alert severity="info" icon={false}>
                                {t("fluss.node_panel.approval.outcomes", {
                                    defaultValue: "Resultados possíveis: Aprovar / Rejeitar / Solicitar mais informações. Conecte cada resultado ao próximo gateway.",
                                })}
                            </Alert>
                        </Stack>
                    ),
                }]}
            />
        );
    }

    const workTab = (
        <Stack spacing={1.25}>
            {(waitForCompletion || Boolean(binding?.recordUpdates)) && <RecordSourceBindingField selection={selection} modeler={modeler} kind="assignment" readOnly={readOnly} onChanged={onChanged} />}
            <NodeNameField {...props} />
            {taskTextFields}
            <FlussNodePanelField label={t("fluss.node_panel.work_type", { defaultValue: "Work type" })}>
                <TextField
                    select
                    size="small"
                    value={taskKind}
                    disabled={readOnly}
                    onChange={(event) => {
                        const next = event.target.value;
                        setTaskKind(next);
                        changeBinding(next, { kind: next });
                    }}
                >
                    <MenuItem value="assignment">
                        {t("fluss.node_panel.work_type.task", { defaultValue: "User task" })}
                    </MenuItem>
                    <MenuItem value="approval">
                        {t("fluss.node_panel.work_type.approval", { defaultValue: "Approval" })}
                    </MenuItem>
                </TextField>
            </FlussNodePanelField>
            <FlussNodePanelField
                label={t("fluss.node_panel.user_task.work_source", { defaultValue: "Task content" })}
            >
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={workSource}
                    disabled={readOnly}
                    onChange={(_, next) => {
                        if (!next || next === workSource) return;
                        setWorkSource(next);
                        if (next === "form") {
                            setTargetTable("");
                            setMappings([]);
                            changeBinding("assignment", {
                                target_table: "", field_mappings: "",
                                formId, formMode: formMode || "create",
                            });
                        } else {
                            setFormId("");
                            setFormMode("create");
                            changeBinding("assignment", {
                                formId: "", formMode: "",
                                target_table: targetTable, field_mappings: serializeMapping(mappings),
                            });
                        }
                    }}
                >
                    <ToggleButton value="record">
                        {t("fluss.node_panel.user_task.work_source.record", { defaultValue: "Mapped record" })}
                    </ToggleButton>
                    <ToggleButton value="form">
                        {t("fluss.node_panel.user_task.work_source.form", { defaultValue: "Form" })}
                    </ToggleButton>
                </ToggleButtonGroup>
            </FlussNodePanelField>
            {workSource === "form" ? (
                <>
                    <FlussNodePanelField label={t("fluss.node_panel.user_task.form", { defaultValue: "Form" })}>
                        <Autocomplete
                            size="small"
                            fullWidth
                            disabled={readOnly}
                            options={data.serviceForms.filter((form) => form.active !== false && form.published !== false)}
                            value={data.serviceForms.find((form) => form.id === formId) || null}
                            getOptionLabel={(form) => String(t(form.title || form.name, { defaultValue: form.title || form.name }))}
                            isOptionEqualToValue={(option, current) => option.id === current.id}
                            onChange={(_, form) => {
                                setFormId(form?.id || "");
                                changeBinding("assignment", { formId: form?.id || "", formMode: formMode || "create" });
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label={t("fluss.node_panel.user_task.form", { defaultValue: "Form" })} />
                            )}
                        />
                    </FlussNodePanelField>
                    <FlussNodePanelField label={t("fluss.node_panel.user_task.form_mode", { defaultValue: "Mode" })}>
                        <TextField
                            select
                            size="small"
                            label={t("fluss.node_panel.user_task.form_mode", { defaultValue: "Mode" })}
                            value={formMode || "create"}
                            disabled={readOnly}
                            onChange={(event) => {
                                const next = event.target.value;
                                setFormMode(next);
                                changeBinding("assignment", { formId, formMode: next });
                            }}
                        >
                            <MenuItem value="create">
                                {t("fluss.node_panel.user_task.form_mode.create", { defaultValue: "Create" })}
                            </MenuItem>
                            <MenuItem value="update">
                                {t("fluss.node_panel.user_task.form_mode.update", { defaultValue: "Update" })}
                            </MenuItem>
                        </TextField>
                        <Typography variant="caption" color="text.secondary">
                            {formMode === "update"
                                ? t("fluss.node_panel.user_task.form_mode.update_help", {
                                    defaultValue: "Opens filled in with the process' own source record. The form must manage the same table.",
                                })
                                : t("fluss.node_panel.user_task.form_mode.create_help", {
                                    defaultValue: "Opens blank; submitting it creates a new record in the form's own table.",
                                })}
                        </Typography>
                    </FlussNodePanelField>
                </>
            ) : (
                <>
                    <FlussNodePanelField
                        label={t("fluss.node_panel.user_task.create_in", { defaultValue: "Create record in" })}
                    >
                        <TableSelect
                            value={targetTable}
                            tables={data.tables}
                            label={String(t("fluss.node_panel.user_task.create_in", { defaultValue: "Create record in" }))}
                            readOnly={readOnly}
                            onChange={(next) => {
                                setTargetTable(next);
                                setMappings([]);
                                changeBinding("assignment", {
                                    target_table: next,
                                    field_mappings: "",
                                });
                            }}
                        />
                    </FlussNodePanelField>
                    <FlussNodePanelField
                        label={t("fluss.node_panel.user_task.created_fields", {
                            defaultValue: "Created record fields",
                        })}
                        fieldPath="field_mappings"
                    >
                        {/* PRIM-015 (round 6): this is the ONE FieldMapper call site in the whole panel
                            that must keep enforceRequired at its default (true). This mapping CREATES a
                            record in targetTable, so its required columns genuinely need to be populated
                            or the record can't be persisted. Do not "uniformize" this with the other
                            FieldMapper call sites below (Output variables, recordUpdates, payload, etc.)
                            — those map to variables or update an already-existing record, which is a
                            different semantics. See round 6 plan, Bloco A, call site #1. */}
                        <FieldMapper
                            value={mappings.filter((mapping) => mapping.target !== "sys_class_name")}
                            targetFields={visibleFields}
                            templateText
                            disabled={readOnly}
                            onChange={(next) => {
                                const withClass = [...next, ...mappings.filter((mapping) => mapping.target === "sys_class_name")];
                                setMappings(withClass);
                                changeBinding("assignment", {
                                    target_table: targetTable,
                                    field_mappings: serializeMapping(withClass),
                                });
                            }}
                        />
                    </FlussNodePanelField>
                </>
            )}
            <FlussNodePanelField
                label={t("fluss.node_panel.user_task.wait_for_completion", { defaultValue: "Wait for completion" })}
                fieldPath="waitCondition" issuePrefix
            >
                <FormControlLabel
                    control={(
                        <Switch
                            checked={waitForCompletion}
                            disabled={readOnly}
                            onChange={(event) => {
                                const checked = event.target.checked;
                                const nextCondition = checked ? waitCondition : "";
                                setWaitForCompletion(checked);
                                setWaitCondition(nextCondition);
                                changeBinding("assignment", {
                                    waitForCompletion: checked ? "true" : "",
                                    waitCondition: nextCondition,
                                });
                            }}
                        />
                    )}
                    label={t("fluss.node_panel.user_task.wait_for_completion", { defaultValue: "Wait for completion" })}
                />
                {!waitForCompletion && (
                    <Typography variant="body2" color="text.secondary">
                        {t("fluss.node_panel.user_task.no_wait_boundary_help", { defaultValue: "Sem esperar a conclusão, o fluxo segue imediatamente e eventos Boundary anexados não são ativados." })}
                    </Typography>
                )}
                {waitForCompletion && (
                    <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("fluss.node_panel.user_task.wait_help", { defaultValue: "Aguardar até que o registro da tarefa indique sua conclusão. Ajuste o campo e o valor conforme os estados desse registro." })}
                        </Typography>
                        <FlussConditionBuilder
                            value={waitCondition}
                            recordTable={waitRecordTable}
                            readOnly={readOnly}
                            onChange={(next) => {
                                const serialized = typeof next === "string" ? next : JSON.stringify(next);
                                setWaitCondition(serialized);
                                changeBinding("assignment", { waitForCompletion: "true", waitCondition: serialized });
                            }}
                        />
                        {!waitCondition && <Typography variant="caption" color="warning.main">
                            {t("fluss.node_panel.user_task.wait_condition_required", { defaultValue: "Escolha quando a tarefa deve ser concluída; sem uma condição, o processo não pode ser publicado." })}
                        </Typography>}
                    </Stack>
                )}
            </FlussNodePanelField>
        </Stack>
    );

    const assignmentTab = (
        <Stack spacing={1.25}>
            <FlussNodePanelField
                label={t("fluss.node_panel.user_task.assignment", { defaultValue: "Assignment" })}
                fieldPath="assigneeExpression"
            >
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={assignment?.mode || "queue"}
                    disabled={readOnly}
                    onChange={(_, mode) => mode && changeAssignment(
                        mode === "queue"
                            ? { mode: "queue", roleName: data.queues[0]?.roleName || "" }
                            : mode === "fixed_user"
                                ? { mode: "fixed_user", userId: "" }
                                : { mode: "record_person", source: undefined }
                    )}
                >
                    <ToggleButton value="queue">
                        {t("fluss.node_panel.assignment.queue", { defaultValue: "Queue" })}
                    </ToggleButton>
                    <ToggleButton value="record_person">
                        {t("fluss.node_panel.assignment.person", { defaultValue: "Person from record" })}
                    </ToggleButton>
                    <ToggleButton value="fixed_user">
                        {t("fluss.node_panel.assignment.fixed_user", { defaultValue: "Fixed user" })}
                    </ToggleButton>
                </ToggleButtonGroup>
                <QueueSelect
                    value={assignment}
                    onChange={changeAssignment}
                    roles={data.roles}
                    groups={data.groups}
                    groupRoles={data.groupRoles}
                    lockedMode={assignment?.mode || "queue"}
                    disabled={readOnly}
                />
                {assignment?.mode === "queue" && assignment.roleName && (
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.assignment.queue_rule", {
                            role: queueRoleLabel,
                            defaultValue: "Anyone holding the role {{role}} sees this task in My tasks — whether the role is assigned directly, through a group, or inherited through the role hierarchy. There is no extra record filter: the assignment on this node is what puts the task in the queue.",
                        })}
                    </Typography>
                )}
                {assignment?.mode === "queue" && assignment.roleName && workSource === "record" && targetTable && (
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.assignment.queue_rule_work_record", {
                            table: targetTable,
                            defaultValue: "This task also writes a record in {{table}} with assigned_to/assignment_group. Who can see that record is decided by the row-level ACL configured for the table in metadata, not by this screen.",
                        })}
                    </Typography>
                )}
            </FlussNodePanelField>
        </Stack>
    );

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="user_task"
            canViewAdvanced={canViewAdvanced}
            advancedContent={(
                <Stack spacing={1.25}>
                    {workSource === "record" && hasClassField && (
                        <FlussNodePanelField label={t("fluss.node_panel.user_task.record_class", { defaultValue: "Record class" })}>
                            <TextField size="small" value={targetTable} disabled fullWidth />
                        </FlussNodePanelField>
                    )}
                    <FlussNodePanelField
                        label={t("fluss.node_panel.user_task.output_variables", {
                            defaultValue: "Output variables",
                        })}
                        fieldPath="variableMappings"
                    >
                        {/* PRIM-015 (round 6, call site #2): targets are process variables, not
                            columns of a record being created — ScopeOption.required doesn't even
                            apply here (it comes from column is_mandatory), so this must never
                            enforce. */}
                        <FieldMapper
                            value={outputMappings}
                            targetFields={outputTargets}
                            recordTable={waitRecordTable}
                            disabled={readOnly}
                            enforceRequired={false}
                            onChange={(next) => {
                                setOutputMappings(next);
                                changeBinding("assignment", {
                                    variableMappings: serializeMapping(next),
                                });
                            }}
                        />
                    </FlussNodePanelField>
                </Stack>
            )}
            tabs={[
                {
                    key: "work",
                    label: t("fluss.node_panel.tab.work", { defaultValue: "Work" }),
                    content: workTab,
                },
                {
                    key: "assignment",
                    label: t("fluss.node_panel.tab.assignment", { defaultValue: "Assignment" }),
                    content: assignmentTab,
                },
            ]}
        />
    );
};

export const ManualTaskNodePanel = (props: ActivityPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, readOnly, onChanged } = props;
    const binding = primaryBindingFor(selection.businessObject);
    const [assignment, setAssignment] = useState<QueueSelection>(assignmentFromBinding(binding));
    const [description, setDescription] = useState(String(binding?.description || ""));
    const [taskTitle, setTaskTitle] = useState(String(binding?.taskTitle || ""));
    const [instructions, setInstructions] = useState(String(binding?.instructions || ""));

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setAssignment(assignmentFromBinding(current));
        setDescription(String(current?.description || ""));
        setTaskTitle(String(current?.taskTitle || ""));
        setInstructions(String(current?.instructions || ""));
    }, [selection.id, selection.businessObject]);

    const changeBinding = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, "assignment", updates);
        onChanged();
    };

    const changeAssignment = (next: QueueSelection) => {
        setAssignment(next);
        if (next?.mode === "queue") {
            changeBinding({ assigneeRole: next.roleName, assigneeUser: "", assigneeExpression: "" });
        } else if (next?.mode === "fixed_user") {
            changeBinding({ assigneeRole: "", assigneeUser: next.userId || "", assigneeExpression: "" });
        } else {
            changeBinding({ assigneeRole: "", assigneeUser: "", assigneeExpression: (next?.mode === "record_person" && next.source) || "" });
        }
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="manual_task"
            canViewAdvanced={props.canViewAdvanced}
            tabs={[{
                key: "manual_task",
                label: t("fluss.node_panel.type.manual_task", { defaultValue: "Manual task" }),
                content: (
                    <Stack spacing={1.25}>
                        <NodeNameField {...props} />
                        <FlussNodePanelField label={t("fluss.template.task_title", { defaultValue: "Task title" })} fieldPath="taskTitle">
                            <TemplateTextField label={String(t("fluss.template.task_title", { defaultValue: "Task title" }))} value={taskTitle} disabled={readOnly}
                                onChange={(next) => { setTaskTitle(next); changeBinding({ taskTitle: next }); }} />
                        </FlussNodePanelField>
                        <FlussNodePanelField label={t("fluss.template.instructions", { defaultValue: "Instructions" })} fieldPath="instructions">
                            <TemplateTextField label={String(t("fluss.template.instructions", { defaultValue: "Instructions" }))} value={instructions} multiline disabled={readOnly}
                                onChange={(next) => { setInstructions(next); changeBinding({ instructions: next }); }} />
                        </FlussNodePanelField>
                        <FlussNodePanelField label={t("fluss.template.manual_description", { defaultValue: "Description" })}>
                            <TranslatableInput
                                label={t("fluss.template.manual_description", { defaultValue: "Description" })}
                                value={description}
                                suggestedKey={`fluss_manual_task.${selection.id}.description`}
                                multiline
                                helperText={String(t("fluss.node_panel.manual_task.description.help", { defaultValue: "Exibida na tarefa da caixa de entrada." }))}
                                disabled={readOnly}
                                onChange={(value) => {
                                    setDescription(value);
                                    changeBinding({ description: value });
                                }}
                            />
                        </FlussNodePanelField>
                        <FlussNodePanelField label={t("fluss.node_panel.manual_task.assignment", { defaultValue: "Assignment" })} fieldPath="assigneeExpression">
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                size="small"
                                value={assignment?.mode || "queue"}
                                disabled={readOnly}
                                onChange={(_, mode) => mode && changeAssignment(
                                    mode === "queue"
                                        ? { mode: "queue", roleName: data.queues[0]?.roleName || "" }
                                        : mode === "fixed_user"
                                            ? { mode: "fixed_user", userId: "" }
                                            : { mode: "record_person", source: undefined }
                                )}
                            >
                                <ToggleButton value="queue">
                                    {t("fluss.node_panel.assignment.queue", { defaultValue: "Queue" })}
                                </ToggleButton>
                                <ToggleButton value="record_person">
                                    {t("fluss.node_panel.assignment.person", { defaultValue: "Person from record" })}
                                </ToggleButton>
                                <ToggleButton value="fixed_user">
                                    {t("fluss.node_panel.assignment.fixed_user", { defaultValue: "Fixed user" })}
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <QueueSelect
                                value={assignment}
                                onChange={changeAssignment}
                                roles={data.roles}
                                groups={data.groups}
                                groupRoles={data.groupRoles}
                                lockedMode={assignment?.mode || "queue"}
                                disabled={readOnly}
                            />
                        </FlussNodePanelField>
                    </Stack>
                ),
            }]}
        />
    );
};

export const ServiceTaskNodePanel = (props: ActivityPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const binding = primaryBindingFor(selection.businessObject);
    const [businessRule, setBusinessRule] = useState(String(binding?.businessRule || ""));
    const [actionId, setActionId] = useState(String(binding?.actionId || ""));
    const [params, setParams] = useState<Record<string, any>>(parseFactoryParams(binding?.params));
    const [outputMappings, setOutputMappings] = useState<FieldMapping[]>(parseMapping(binding?.variableMappings));
    const [specificMode, setSpecificMode] = useState(Boolean(binding?.businessRule && !binding?.actionId));
    const catalog = useDataCatalogContext();
    const sourceOutput = [...catalog.record.outputs, ...catalog.process_variables.outputs,
        ...catalog.nodes.flatMap((node) => node.outputs)].find((output) => output.path === binding?.recordSource);
    const [selectedRecordTable, setSelectedRecordTable] = useState(
        sourceOutput?.referenced_table_name || (binding?.recordSource ? "" : catalog.record.table || resolveAnchorTable(modeler) || "")
    );

    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setBusinessRule(String(current?.businessRule || ""));
        setActionId(String(current?.actionId || ""));
        setParams(parseFactoryParams(current?.params));
        setOutputMappings(parseMapping(current?.variableMappings));
        setSpecificMode(Boolean(current?.businessRule && !current?.actionId));
        const selectedOutput = [...catalog.record.outputs, ...catalog.process_variables.outputs,
            ...catalog.nodes.flatMap((node) => node.outputs)].find((output) => output.path === current?.recordSource);
        setSelectedRecordTable(selectedOutput?.referenced_table_name || (current?.recordSource ? "" : catalog.record.table || resolveAnchorTable(modeler) || ""));
    }, [selection.id, selection.businessObject]);

    const updateActionParams = (next: Record<string, any>) => {
        setParams(next);
        updateBinding(selection, modeler, "service", { params: JSON.stringify(next) });
        onChanged();
    };
    const availableActions = factoryActionsForNodeKind(data.factoryActions, "service_task");
    const selectedAction = data.factoryActions.find((action) => action.id === actionId);
    const actionUsesRecord = selectedAction && (
        (selectedAction.name === "update_record" && params.target === "record") ||
        (selectedAction.name === "dispatch_form" && params.mode === "update") ||
        selectedAction.name === "send_email" || selectedAction.name === "emit_event"
    );
    const selectedOutputs = specificMode ? [] : (catalog.self?.outputs || []).map((output) => output.key);
    const processVariables = processVariableOptions(catalog);

    const recordSourceField = <RecordSourceBindingField selection={selection} modeler={modeler} kind="service" readOnly={readOnly} onChanged={onChanged}
        onResolved={(info) => setSelectedRecordTable(info.referencedTable || "")}
        onSelected={(value, info) => {
            const table = value ? info?.referencedTable || "" : catalog.record.table || "";
            setSelectedRecordTable(table);
            if (selectedAction?.name === "update_record" && params.target === "record" && table) {
                updateActionParams({ ...params, table });
            }
        }} />;

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="service_task"
            canViewAdvanced={canViewAdvanced}
            advancedContent={selectedOutputs.length > 0 ? (
                <FlussNodePanelField
                    label={t("fluss.node_panel.service.output_variables", {
                        defaultValue: "Output variables",
                    })}
                >
                    <ServiceOutputMapper
                        value={outputMappings}
                        outputs={selectedOutputs}
                        variables={processVariables}
                        disabled={readOnly}
                        onChange={(next) => {
                            setOutputMappings(next);
                            updateBinding(selection, modeler, "service", {
                                variableMappings: serializeMapping(next),
                            });
                            onChanged();
                        }}
                    />
                </FlussNodePanelField>
            ) : undefined}
            tabs={[{
                key: "service",
                label: t("fluss.node_panel.tab.service", { defaultValue: "Service" }),
                content: (
                    <Stack spacing={1.25}>
                        <NodeNameField {...props} />
                        {(specificMode || (actionUsesRecord && selectedAction?.name !== "update_record")) && recordSourceField}
                        {!specificMode && (
                            <>
                                <Typography variant="subtitle2">
                                    {t("fluss.node_panel.service.actions", { defaultValue: "Actions" })}
                                </Typography>
                                {availableActions.length === 0 && (
                                    <Alert severity="info">
                                        {t("fluss.node_panel.service.no_actions", { defaultValue: "No active factory actions are available." })}
                                    </Alert>
                                )}
                                <FactoryActionCards
                                    actions={availableActions}
                                    selectedId={actionId}
                                    disabled={readOnly}
                                    onSelect={(action) => {
                                        setActionId(action.id);
                                        setParams({});
                                        setBusinessRule("");
                                        setOutputMappings([]);
                                        updateBinding(selection, modeler, "service", {
                                            actionId: action.id,
                                            params: "{}",
                                            businessRule: "",
                                            variableMappings: "",
                                        });
                                        onChanged();
                                    }}
                                />
                                {selectedAction && (
                                    <FactoryActionParamForm
                                        key={selectedAction.id}
                                        action={selectedAction}
                                        params={params}
                                        data={data}
                                        disabled={readOnly}
                                        recordTableName={selectedRecordTable}
                                        recordSourceField={selectedAction.name === "update_record" ? recordSourceField : undefined}
                                        onChange={updateActionParams}
                                    />
                                )}
                                <Button
                                    variant="text"
                                    size="small"
                                    sx={{ alignSelf: "flex-start", px: 0 }}
                                    onClick={() => setSpecificMode(true)}
                                >
                                    {t("fluss.node_panel.service.use_specific", { defaultValue: "Use a specific Business Rule" })}
                                </Button>
                            </>
                        )}
                        {specificMode && (
                            <>
                                <FlussNodePanelField label={t("fluss.node_panel.service.business_rule", { defaultValue: "Specific Business Rule" })}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        disabled={readOnly}
                                        options={data.businessRules}
                                        value={data.businessRules.find((rule) => rule.id === businessRule) || null}
                                        getOptionLabel={(option) => t(String(option.label)) || option.name}
                                        isOptionEqualToValue={(option, current) => option.id === current.id}
                                        onChange={(_, option) => {
                                            const next = option?.id || "";
                                            setBusinessRule(next);
                                            setActionId("");
                                            setOutputMappings([]);
                                            updateBinding(selection, modeler, "service", { businessRule: next, actionId: "", params: "", variableMappings: "" });
                                            onChanged();
                                        }}
                                        renderInput={(input) => <TextField {...input} />}
                                    />
                                </FlussNodePanelField>
                                <Typography variant="caption" color="text.secondary">
                                    {t("fluss.node_panel.service.specific_help", { defaultValue: "Advanced path for a custom audited Business Rule." })}
                                </Typography>
                                <Button variant="text" size="small" sx={{ alignSelf: "flex-start", px: 0 }} onClick={() => setSpecificMode(false)}>
                                    {t("fluss.node_panel.service.back_to_actions", { defaultValue: "Back to factory actions" })}
                                </Button>
                            </>
                        )}
                    </Stack>
                ),
            }]}
        />
    );
};

export const ScriptTaskNodePanel = (props: ActivityPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const catalog = useDataCatalogContext();
    const processVariables = processVariableOptions(catalog);
    const { data: allTables = [] } = useTables();
    const { data: allColumns = [] } = useColumns();
    const [recordTable, setRecordTable] = useState<string | null>(null);
    const editorRecord = useMemo(() => ({ target_table: recordTable }), [recordTable]);
    const binding = primaryBindingFor(selection.businessObject);
    const scriptValue = (raw: unknown) => String((raw as any)?.body || raw || "");
    const parseCapabilities = (raw: unknown) => {
        try {
            const parsed = JSON.parse(String(raw || "[]"));
            return Array.isArray(parsed) ? parsed.join("\n") : "";
        } catch (_error) {
            return "";
        }
    };
    const [script, setScript] = useState(scriptValue(binding?.script));
    const [capabilities, setCapabilities] = useState(() => parseCapabilities(binding?.capabilities));
    const [timeoutMs, setTimeoutMs] = useState(String(binding?.timeout_ms || "5000"));
    const [outputMappings, setOutputMappings] = useState<FieldMapping[]>(parseMapping(binding?.variableMappings));
    const [abortOnError, setAbortOnError] = useState(String(binding?.abort_on_error ?? "true") !== "false");

    useEffect(() => {
        setRecordTable(null);
        const current = primaryBindingFor(selection.businessObject);
        setScript(scriptValue(current?.script));
        setCapabilities(parseCapabilities(current?.capabilities));
        setTimeoutMs(String(current?.timeout_ms || "5000"));
        setOutputMappings(parseMapping(current?.variableMappings));
        setAbortOnError(String(current?.abort_on_error ?? "true") !== "false");
    }, [selection.id, selection.businessObject]);

    const save = (updates: Record<string, unknown>) => {
        const scriptUpdate = updates.script;
        if (scriptUpdate !== undefined) {
            const modeling = modeler?.get("modeling");
            const moddle = modeler?.get("moddle");
            const nextBinding = ensureBinding(selection, modeler, "script");
            if (modeling && moddle && nextBinding) {
                const nextScript = String(scriptUpdate || "");
                modeling.updateModdleProperties(selection.element, nextBinding, {
                    script: nextScript ? moddle.create("fluss:Script", { body: nextScript }) : undefined,
                });
            }
            const { script: _script, ...rest } = updates;
            if (Object.keys(rest).length > 0) updateBinding(selection, modeler, "script", rest);
        } else {
            updateBinding(selection, modeler, "script", updates);
        }
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="script_task"
            canViewAdvanced={canViewAdvanced}
            advancedContent={(
                <Stack spacing={1.25}>
                    <FlussNodePanelField label={t("fluss.node_panel.script.capabilities", { defaultValue: "Capabilities" })}>
                        <TextField
                            multiline
                            minRows={3}
                            size="small"
                            value={capabilities}
                            disabled={readOnly}
                            inputProps={{ "aria-label": String(t("fluss.node_panel.script.capabilities", { defaultValue: "Capabilities" })) }}
                            placeholder={String(t("fluss.node_panel.script.capabilities_placeholder", { defaultValue: "db.read:self" }))}
                            onChange={(event) => {
                                const next = event.target.value;
                                setCapabilities(next);
                                save({ capabilities: JSON.stringify(next.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean)) });
                            }}
                        />
                    </FlussNodePanelField>
                    <FlussNodePanelField label={t("fluss.node_panel.script.timeout_ms", { defaultValue: "Timeout (ms)" })}>
                        <TextField
                            type="number"
                            size="small"
                            value={timeoutMs}
                            disabled={readOnly}
                            inputProps={{ min: 1, max: 30000, "aria-label": String(t("fluss.node_panel.script.timeout_ms", { defaultValue: "Timeout (ms)" })) }}
                            onChange={(event) => {
                                setTimeoutMs(event.target.value);
                                save({ timeout_ms: event.target.value });
                            }}
                        />
                    </FlussNodePanelField>
                    <FlussNodePanelField
                        label={t("fluss.node_panel.service.output_variables", {
                            defaultValue: "Output variables",
                        })}
                    >
                        <ServiceOutputMapper
                            value={outputMappings}
                            outputs={[]}
                            variables={processVariables}
                            disabled={readOnly}
                            allowCustomOutputs
                            onChange={(next) => {
                                setOutputMappings(next);
                                save({ variableMappings: serializeMapping(next) });
                            }}
                        />
                    </FlussNodePanelField>
                    <FormControlLabel
                        control={<Switch checked={abortOnError} disabled={readOnly} onChange={(_, checked) => { setAbortOnError(checked); save({ abort_on_error: checked ? "true" : "false" }); }} />}
                        label={t("fluss.node_panel.script.abort_on_error", { defaultValue: "Abort on error" })}
                    />
                </Stack>
            )}
            tabs={[{
                key: "script",
                label: t("fluss.node_panel.tab.script", { defaultValue: "Script" }),
                content: (
                    <Stack spacing={1.25}>
                        <NodeNameField {...props} />
                        <RecordSourceBindingField selection={selection} modeler={modeler} kind="script" readOnly={readOnly} onChanged={onChanged}
                            onResolved={(info) => setRecordTable(info.referencedTable || null)}
                            onSelected={(value, info) => setRecordTable(value ? info?.referencedTable || null : null)} />
                        <FlussNodePanelField label={t("fluss.node_panel.script.code", { defaultValue: "Script" })}>
                            <div style={{ height: 260 }}>
                                <StudioCodeEditor
                                    table={null}
                                    column={null}
                                    value={script}
                                    record={editorRecord}
                                    allTables={allTables}
                                    allColumns={allColumns}
                                    profileId="fluss.script_task"
                                    modelKey={`script_task/${selection.id}`}
                                    processVariables={catalog.process_variables.outputs}
                                    readOnly={readOnly}
                                    onChange={(next) => {
                                        setScript(next);
                                        save({ script: next });
                                    }}
                                />
                            </div>
                        </FlussNodePanelField>
                    </Stack>
                ),
            }]}
        />
    );
};

export const BusinessRuleTaskNodePanel = (props: ActivityPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const binding = primaryBindingFor(selection.businessObject);
    const [businessRule, setBusinessRule] = useState(String(binding?.businessRule || ""));

    useEffect(() => {
        setBusinessRule(String(primaryBindingFor(selection.businessObject)?.businessRule || ""));
    }, [selection.id, selection.businessObject]);

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="business_rule_task"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "business_rule",
                label: t("fluss.node_panel.tab.business_rule", { defaultValue: "Business Rule" }),
                content: (
                    <Stack spacing={1.25}>
                        <NodeNameField {...props} />
                        <RecordSourceBindingField selection={selection} modeler={modeler} kind="service" readOnly={readOnly} onChanged={onChanged} />
                        <FlussNodePanelField label={t("fluss.node_panel.service.business_rule", { defaultValue: "Specific Business Rule" })}>
                            <Autocomplete
                                size="small"
                                fullWidth
                                disabled={readOnly}
                                options={data.businessRules}
                                value={data.businessRules.find((rule) => rule.id === businessRule) || null}
                                getOptionLabel={(option) => t(String(option.label)) || option.name}
                                isOptionEqualToValue={(option, current) => option.id === current.id}
                                onChange={(_, option) => {
                                    const next = option?.id || "";
                                    setBusinessRule(next);
                                    updateBinding(selection, modeler, "service", {
                                        businessRule: next,
                                        actionId: "",
                                        params: "",
                                        variableMappings: "",
                                    });
                                    onChanged();
                                }}
                                renderInput={(input) => <TextField {...input} />}
                            />
                        </FlussNodePanelField>
                    </Stack>
                ),
            }]}
        />
    );
};
